/**
 * Centralized date formatting utilities
 */

import { intlLocale } from '@/services/i18n/languages'

/**
 * Templates for the relative-time units. Each uses `{{n}}` as the count
 * placeholder. Defaults reproduce the original English compact style; pass
 * localized templates (e.g. from i18next) to translate the "ago" suffix.
 */
export interface RelativeFormats {
  minutes: string
  hours: string
  days: string
  weeks: string
}

const DEFAULT_RELATIVE_FORMATS: RelativeFormats = {
  minutes: '{{n}}m ago',
  hours: '{{n}}h ago',
  days: '{{n}}d ago',
  weeks: '{{n}}w ago',
}

/**
 * Format a date as relative time (e.g., "5m ago", "2h ago", "3d ago")
 */
export function formatRelative(
  dateString: string,
  locale: string,
  justNowText = 'just now',
  formats: RelativeFormats = DEFAULT_RELATIVE_FORMATS
): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  const fmt = (template: string, n: number) => template.replace('{{n}}', String(n))

  if (diffMins < 1) return justNowText
  if (diffMins < 60) return fmt(formats.minutes, diffMins)

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return fmt(formats.hours, diffHours)

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return fmt(formats.days, diffDays)
  if (diffDays < 30) return fmt(formats.weeks, Math.floor(diffDays / 7))

  return date.toLocaleDateString(intlLocale(locale), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Format a date as a localized date string (e.g., "15 Apr 2024")
 */
export function formatDate(dateString: string, locale: string): string {
  return new Date(dateString).toLocaleDateString(intlLocale(locale), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * The formatter of one style for a locale, built once per locale: building an
 * `Intl.DateTimeFormat` costs far more than using it.
 */
function localeFormat(
  options: Intl.DateTimeFormatOptions
): (locale: string) => Intl.DateTimeFormat {
  const formats = new Map<string, Intl.DateTimeFormat>()
  return locale => {
    const tag = intlLocale(locale)
    let format = formats.get(tag)
    if (!format) {
      format = new Intl.DateTimeFormat(tag, options)
      formats.set(tag, format)
    }
    return format
  }
}

const dateTimeFormat = localeFormat({ dateStyle: 'medium', timeStyle: 'short' })

/**
 * Format a timestamp as a localized date and time without seconds (e.g., "15 Apr 2024, 09:42")
 */
export function formatDateTimeLocalized(dateString: string, locale: string): string {
  return dateTimeFormat(locale).format(new Date(dateString))
}

const exactDateTimeFormat = localeFormat({
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

/**
 * Format a timestamp to the second, on a 24-hour clock with a numeric date (e.g. "20. 10. 2026
 * 14:32:05"): every value of a locale has the same width, so a column of them lines up.
 */
export function formatExactDateTime(dateString: string, locale: string): string {
  return exactDateTimeFormat(locale).format(new Date(dateString))
}

const historyTimeFormat = localeFormat({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
const historyTimestampFormat = localeFormat({
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  timeZoneName: 'shortOffset',
})

export function formatHistoryTime(value: string, locale: string): string {
  return historyTimeFormat(locale).format(new Date(value))
}

/** Exact recorded time, including the local UTC offset (also disambiguates DST changes). */
export function formatHistoryTimestamp(value: string, locale: string): string {
  return historyTimestampFormat(locale).format(new Date(value))
}

/**
 * Format a date as ISO datetime with UTC (e.g., "2024-04-15 09:42 UTC")
 */
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return `${date.toISOString().replace('T', ' ').slice(0, 16)} UTC`
}

/**
 * Get duration since a date as a short string (e.g., "Today", "3d", "2w", "1mo")
 */
export function getDuration(createdAt: string, todayText = 'Today'): string {
  const created = new Date(createdAt)
  const now = new Date()
  const diffMs = now.getTime() - created.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return todayText
  if (diffDays === 1) return '1d'
  if (diffDays < 7) return `${diffDays}d`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w`
  return `${Math.floor(diffDays / 30)}mo`
}

/**
 * Get the month abbreviation from a date (e.g., "Apr")
 */
export function getMonthAbbrev(dateString: string, locale: string): string {
  return new Date(dateString).toLocaleDateString(intlLocale(locale), { month: 'short' })
}

const monthNameFormats = new Map<string, Intl.DateTimeFormat>()

/**
 * The twelve month names of a locale, January first — what a calendar header
 * and a month grid need. `long` gives "January" / "január", `short` the
 * compact "Jan" / "jan" form.
 */
export function getMonthNames(locale: string, style: 'long' | 'short'): string[] {
  const tag = intlLocale(locale)
  const key = `${tag}|${style}`
  let format = monthNameFormats.get(key)
  if (!format) {
    format = new Intl.DateTimeFormat(tag, { month: style })
    monthNameFormats.set(key, format)
  }
  // Day 1 of a fixed year: the date only exists to pick out the month name.
  return Array.from({ length: 12 }, (_, month) => format.format(new Date(2021, month, 1)))
}

const pad2 = (value: number) => String(Math.abs(value)).padStart(2, '0')

/**
 * "2026-09-30T14:05:00+02:00": the local wall-clock time with its own UTC offset. With `millis`
 * the milliseconds too ("…14:05:00.000+02:00"), for a bound that must not lose them.
 */
export function toLocalIso(date: Date, millis = false): string {
  const offset = -date.getTimezoneOffset()
  const sign = offset < 0 ? '-' : '+'
  const day = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
  const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`
  const fraction = millis ? `.${String(date.getMilliseconds()).padStart(3, '0')}` : ''
  return `${day}T${time}${fraction}${sign}${pad2(Math.trunc(offset / 60))}:${pad2(offset % 60)}`
}

/**
 * The `<input type="datetime-local">` value of an ISO time, in the device's time zone — to the
 * minute, or with `seconds` to the second (an input with `step="1"`).
 */
export function toLocalInputValue(iso: string, seconds = false): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '' : toLocalIso(date).slice(0, seconds ? 19 : 16)
}

/** A `datetime-local` value back to an ISO time with the offset of that moment (DST-safe). */
export function fromLocalInputValue(value: string): string | null {
  const date = new Date(value)
  return value && !Number.isNaN(date.getTime()) ? toLocalIso(date) : null
}

const clockTimeFormat = localeFormat({
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

/** A moment as the time of day to the second on a 24-hour clock (e.g. "20:56:08"). */
export function formatClockTime(date: Date, locale: string): string {
  return clockTimeFormat(locale).format(date)
}
